import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Minus, Plus, Send, Check, Loader2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { ContactChannels } from "@/components/ContactChannels";
import { formTranslations } from "@/i18n/formPlus";
import { ProjectOptions, EMPTY_PROJECT_DETAILS } from "@/components/ProjectOptions";
import { EmailBriefDialog } from "@/components/EmailBriefDialog";

const CONTACT_EMAIL = "webbuilders.corporate@gmail.com";
const EN_FEATURES = [...translations.en.features, ...formTranslations.en.featureOptions];
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Contact() {
  const { t } = useLanguage();
  const featureLabels = [...t("features"), ...t("formPlus.featureOptions")];
  const [form, setForm] = useState({
    name: "",
    email: "",
    business: "",
    pages: 1,
    features: [],
    message: "",
    ...EMPTY_PROJECT_DETAILS,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [emailPreview, setEmailPreview] = useState(false);

  useEffect(() => {
    const select = (event) => {
      const index = event.detail === 4 ? 6 : event.detail;
      if (!Number.isInteger(index) || index < 0 || index >= EN_FEATURES.length) return;
      setSent(false);
      setForm(f => ({ ...f, features: f.features.includes(index) ? f.features : [...f.features, index] }));
    };
    window.addEventListener("wb-select-service", select);
    return () => window.removeEventListener("wb-select-service", select);
  }, []);

  const toggleFeature = (index) => {
    setForm((f) => ({
      ...f,
      features: f.features.includes(index)
        ? f.features.filter((x) => x !== index)
        : [...f.features, index],
    }));
  };

  const setPages = (delta) => {
    setForm((f) => ({ ...f, pages: Math.min(100, Math.max(1, f.pages + delta)) }));
  };

  const validateForm = () => {
    setError("");
    if (form.name.trim().length < 2) {
      setError(t("contact.errName"));
      document.getElementById("wb-name")?.focus();
      toast.error(t("contact.errName"));
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setError(t("contact.errEmail"));
      document.getElementById("wb-email")?.focus();
      toast.error(t("contact.errEmail"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || !validateForm()) return;
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        name: form.name,
        email: form.email.trim(),
        business: form.business || null,
        pages: form.pages,
        features: form.features.map((i) => EN_FEATURES[i]),
        message: form.message,
        ...Object.fromEntries(Object.keys(EMPTY_PROJECT_DETAILS).map(key => [key, form[key].trim()])),
      }, { timeout: 20000 });
      setSent(true);
      toast.success(t("contact.toastSuccess"));
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail);
      setError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-sm bg-transparent border border-white/20 focus:border-gold focus:outline-none px-4 py-3 text-sm text-white placeholder:text-white/35 transition-colors duration-300";

  return (
    <section id="contact" className="studio-section contact-section" data-testid="contact-section">
      <div className="studio-container grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5 min-w-0">
          <Reveal>
            <Overline>{t("contact.overline")}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="sr-only">{t("contact.overline")}</h2>
            <p className="section-display contact-heading" data-testid="contact-headline">
              {t("contact.h2a")}
              <br />
              <span className="gold-text">{t("contact.h2b")}</span>
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p data-testid="contact-quote-promise" className="mt-6 text-sm text-white/65 leading-relaxed max-w-md">
              {t("contact.body")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              data-testid="contact-email-link"
              href={`mailto:${CONTACT_EMAIL}`}
              className="contact-email"
            >
              <Mail className="h-5 w-5 text-gold" aria-hidden="true" />
              <span>{CONTACT_EMAIL}</span>
            </a>
            <ContactChannels />
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7 min-w-0">
          <div className="contact-form-shell">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="py-10 flex flex-col items-center text-center"
                data-testid="contact-success-panel"
                role="status"
              >
                <div className="h-16 w-16 rounded-full border border-gold/50 bg-gold/10 flex items-center justify-center">
                  <Check className="h-8 w-8 text-gold" aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display font-medium text-2xl">{t("contact.successT")}</h3>
                <p className="mt-3 max-w-sm text-white/65 leading-relaxed">
                  {t("contact.successBody", {
                    name: form.name.split(" ")[0],
                    email: form.email,
                  })}
                </p>
                <button
                  data-testid="contact-send-another-button"
                  onClick={() => {
                    setSent(false);
                    setError("");
                    setForm({ name: "", email: "", business: "", pages: 1, features: [], message: "", ...EMPTY_PROJECT_DETAILS });
                  }}
                  className="mt-8 rounded-sm border border-white/20 hover:border-white px-6 py-3 text-sm text-white/80 transition-colors duration-300 cursor-pointer"
                >
                  {t("contact.sendAnother")}
                </button>
                <button type="button" data-testid="contact-success-email-button" className="text-link mt-6" onClick={() => setEmailPreview(true)}><Mail size={16} />{t("formPlus.emailButton")}</button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} noValidate data-testid="contact-form" aria-describedby={error ? "contact-form-error" : undefined}>
                <p className="contact-form-title" data-testid="contact-form-title">{t("refresh.formTitle")}</p>
                <p className="details-section-label mono-label">01 / {t("contact.overline")}</p>
                {error && <p id="contact-form-error" className="form-error" role="alert" data-testid="contact-form-error">{error}</p>}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="wb-name" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                      {t("contact.name")}
                    </label>
                    <input
                      id="wb-name"
                      data-testid="contact-name-input"
                      type="text"
                      autoComplete="name"
                      maxLength={120}
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("contact.namePh")}
                      className={`${inputClass} mt-2`}
                    />
                  </div>
                  <div>
                    <label htmlFor="wb-email" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                      {t("contact.email")}
                    </label>
                    <input
                      id="wb-email"
                      data-testid="contact-email-input"
                      type="email"
                      autoComplete="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={t("contact.emailPh")}
                      className={`${inputClass} mt-2`}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="wb-business" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                    {t("contact.business")}
                  </label>
                  <input
                    id="wb-business"
                    data-testid="contact-business-input"
                    type="text"
                    autoComplete="organization"
                    maxLength={160}
                    value={form.business}
                    onChange={(e) => setForm({ ...form, business: e.target.value })}
                    placeholder={t("contact.businessPh")}
                    className={`${inputClass} mt-2`}
                  />
                </div>

                <div className="mt-8">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                    {t("contact.pages")}
                  </span>
                  <div className="mt-3 flex items-center gap-5">
                    <button
                      type="button"
                      data-testid="contact-pages-minus-button"
                      onClick={() => setPages(-1)}
                      disabled={form.pages <= 1}
                      className="h-11 w-11 shrink-0 rounded-sm border border-white/20 hover:border-white flex items-center justify-center transition-colors duration-300 cursor-pointer"
                      aria-label={t("refresh.less")}
                    >
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <span data-testid="contact-pages-value" className="font-mono text-2xl text-gold w-10 text-center">
                      {form.pages}
                    </span>
                    <button
                      type="button"
                      data-testid="contact-pages-plus-button"
                      onClick={() => setPages(1)}
                      disabled={form.pages >= 100}
                      className="h-11 w-11 shrink-0 rounded-sm border border-white/20 hover:border-white flex items-center justify-center transition-colors duration-300 cursor-pointer"
                      aria-label={t("refresh.more")}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <span className="text-xs text-white/40">{t("contact.pagesHint")}</span>
                  </div>
                </div>

                <div className="mt-8">
                  <span className="details-section-label mono-label" data-testid="contact-features-label">
                    02 / {t("formPlus.featuresLabel")}
                  </span>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {featureLabels.map((label, i) => {
                      const active = form.features.includes(i);
                      return (
                        <button
                          key={EN_FEATURES[i]}
                          type="button"
                          data-testid={`contact-feature-chip-${slugify(EN_FEATURES[i])}`}
                          onClick={() => toggleFeature(i)}
                          aria-pressed={active}
                          className={`feature-option border px-4 py-2 text-xs transition-colors duration-300 cursor-pointer ${
                            active
                              ? "border-brand bg-brand text-white"
                              : "border-white/20 text-white/70 hover:border-white/50"
                          }`}
                        >
                          <span className="feature-checkbox" aria-hidden="true">{active && <Check size={11} />}</span>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <ProjectOptions form={form} setForm={setForm} inputClass={inputClass} />
                <div className="mt-8">
                  <label htmlFor="wb-message" className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                    {t("contact.message")}
                  </label>
                  <textarea
                    id="wb-message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t("contact.messagePh")}
                    rows={4}
                    maxLength={4000}
                    className={`${inputClass} mt-2 resize-none`}
                  />
                </div>

                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-9 w-full rounded-sm bg-brand hover:bg-brand-bright disabled:opacity-60 text-white font-medium px-7 py-4 text-sm transition-colors duration-300 flex items-center justify-center gap-3 cursor-pointer"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      {t("contact.sending")}
                    </>
                  ) : (
                    <>
                      {t("formPlus.inboxButton")}
                      <Send className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
                    </>
                  )}
                </button>
                <button type="button" data-testid="contact-email-brief-button" className="email-brief-button" onClick={() => { if (validateForm()) setEmailPreview(true); }}><Mail size={17} />{t("formPlus.emailButton")}<span aria-hidden="true">↗</span></button>
                <p data-testid="contact-reply-hint" className="mt-4 text-center text-xs text-white/60">
                  {t("contact.hint")}
                </p>
                <p className="contact-privacy" data-testid="contact-privacy-note">{t("refresh.privacy")}</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
      <EmailBriefDialog open={emailPreview} onOpenChange={setEmailPreview} form={form} features={form.features.map(i => EN_FEATURES[i])} />
    </section>
  );
}
